import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { apiService, AdminToolDefinition, SubscriptionPlanToolListItem } from '../../services/api';

const FORWARDING_TOOL_NAMES = new Set(['outcall', 'warm_transfer', 'callcenter']);

interface PlanToolsDialogProps {
  planId: string | null;
  planName: string;
  /** Pricing flag — null wenn unbekannt. Wenn nicht zur Tool-Whitelist passt → Inkonsistenz-Warnung. */
  externalForwardingEnabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog with a flat checkbox list of all ToolDefinitions for the
 * selected SubscriptionPlan (CA-358). Mirrors the UX of the
 * RegistrationProfilesPage tool picker — pick the tools that should be
 * available, click "Speichern", done. Backend diff-applies (add/remove).
 */
export function PlanToolsDialog({
  planId,
  planName,
  externalForwardingEnabled,
  open,
  onOpenChange,
}: PlanToolsDialogProps) {
  const [allTools, setAllTools] = useState<AdminToolDefinition[]>([]);
  const [initialSelection, setInitialSelection] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !planId) {
      setAllTools([]);
      setInitialSelection(new Set());
      setSelected(new Set());
      setSearch('');
      return;
    }
    setLoading(true);
    void Promise.all([
      apiService.listAvailableToolDefinitions(),
      apiService.listPlanTools(planId),
    ])
      .then(([all, planTools]) => {
        setAllTools(all);
        const ids = new Set(planTools.map((pt: SubscriptionPlanToolListItem) => pt.toolDefinitionId));
        setInitialSelection(ids);
        setSelected(new Set(ids));
      })
      .catch(err => {
        toast.error('Tools konnten nicht geladen werden.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [open, planId]);

  const filteredTools = useMemo(() => {
    // Nur Function-Tools sind im Plan-Picker relevant — APIs/Webhooks/MCP
    // werden customer-spezifisch konfiguriert und nicht plan-gating.
    // System-managed Tools (z.B. set_send_hook für Conditional Hooks)
    // werden ebenfalls ausgeschlossen — sie sind plan-unabhängig
    // immer verfügbar und nicht admin-konfigurierbar.
    const functionTools = allTools.filter(
      t => t.category === 'function' && !t.isSystemManaged,
    );
    const q = search.trim().toLowerCase();
    if (!q) return functionTools;
    return functionTools.filter(t => t.displayName.toLowerCase().includes(q));
  }, [allTools, search]);

  const totalFunctionTools = useMemo(
    () => allTools.filter(t => t.category === 'function' && !t.isSystemManaged).length,
    [allTools],
  );

  /**
   * Live-Mismatch-Detection zwischen aktueller Auswahl + Pricing-Flag.
   * `externalForwardingEnabled` ist ein BILLING-Feld; die Tool-Whitelist
   * steuert Sichtbarkeit. Beide laufen entkoppelt — wenn sie aber für
   * Forwarding-Tools auseinandergehen, gibt es einen Hinweis, damit der
   * Admin entweder das Pricing oder die Whitelist anpasst.
   */
  const forwardingMismatch = useMemo(() => {
    if (externalForwardingEnabled === undefined) return null;
    const selectedForwardingTools = allTools.filter(
      t => selected.has(t.id) && FORWARDING_TOOL_NAMES.has(t.name),
    );
    const hasForwardingTool = selectedForwardingTools.length > 0;
    if (hasForwardingTool && !externalForwardingEnabled) {
      return {
        kind: 'tool-without-billing' as const,
        toolNames: selectedForwardingTools.map(t => t.displayName),
      };
    }
    if (!hasForwardingTool && externalForwardingEnabled) {
      return { kind: 'billing-without-tool' as const };
    }
    return null;
  }, [allTools, selected, externalForwardingEnabled]);

  const toggle = (toolId: string, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(toolId);
      else next.delete(toolId);
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    if (selected.size !== initialSelection.size) return true;
    for (const id of selected) {
      if (!initialSelection.has(id)) return true;
    }
    return false;
  }, [selected, initialSelection]);

  const handleSave = async () => {
    if (!planId || !hasChanges) return;

    const toAdd: string[] = [];
    const toRemove: string[] = [];
    for (const id of selected) if (!initialSelection.has(id)) toAdd.push(id);
    for (const id of initialSelection) if (!selected.has(id)) toRemove.push(id);

    setSaving(true);
    try {
      // Run mutations in parallel — backend operations are idempotent
      await Promise.all([
        ...toAdd.map(id => apiService.addPlanTool(planId, { toolDefinitionId: id })),
        ...toRemove.map(id => apiService.removePlanTool(planId, id)),
      ]);
      toast.success(
        `Tools gespeichert (${toAdd.length} hinzugefügt, ${toRemove.length} entfernt).`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tools für „{planName}" verwalten</DialogTitle>
          <DialogDescription>
            Wähle alle Tools aus, die im Plan freigeschaltet sein sollen. Customers mit
            diesem Plan sehen nur diese Tools im Voicebot-Editor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {forwardingMismatch && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-1">
                <strong>Inkonsistenz mit Abrechnungs-Flag</strong>
                {forwardingMismatch.kind === 'tool-without-billing' && (
                  <p>
                    Forwarding-Tool(s) <em>{forwardingMismatch.toolNames.join(', ')}</em> sind
                    ausgewählt, aber <code>external_forwarding_enabled</code> dieses Plans ist{' '}
                    <strong>aus</strong>. Anrufweiterleitungen werden zwar sichtbar, aber nicht
                    als Pay-per-use-Posten abgerechnet — Plan editieren und Flag aktivieren,
                    falls Abrechnung gewünscht.
                  </p>
                )}
                {forwardingMismatch.kind === 'billing-without-tool' && (
                  <p>
                    <code>external_forwarding_enabled</code> ist <strong>aktiv</strong>, aber
                    kein Forwarding-Tool (outcall / warm_transfer / callcenter) ist im Plan
                    whitelisted. Customers sehen die Forwarding-UI nicht — Tool whitelisten
                    oder Pricing-Flag deaktivieren.
                  </p>
                )}
              </div>
            </div>
          )}

          <Input
            placeholder="Tool suchen…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            disabled={loading}
          />

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTools.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Keine Tools gefunden.
            </p>
          ) : (
            <div className="max-h-[420px] space-y-1.5 overflow-y-auto rounded-lg border p-3">
              {filteredTools.map(tool => (
                <div key={tool.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`plan-tool-${tool.id}`}
                    checked={selected.has(tool.id)}
                    onCheckedChange={c => toggle(tool.id, c === true)}
                  />
                  <Label
                    htmlFor={`plan-tool-${tool.id}`}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {tool.displayName}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground">
            {selected.size} von {totalFunctionTools} ausgewählt
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving || loading}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Speichern…
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
